import React, { useMemo, useState } from "react";
import { useMutation, useQuery } from "react-query";

import { SearchOutlined } from "@ant-design/icons";
// Components
import { message, Modal, Select, Space, Spin, Typography } from "antd";

// Apis
import { convertToVirtualKit, fetchProductsIndex } from "api/fulfillment/product";
// Hooks and Utils
import { useDebounce } from "components/ratecard/hooks";
// types
import { Product } from "interfaces/products";

// Styles
import colors from "../../../../../css/colors";

import * as S from "./ConvertToVirtualKitButton.styles";

import { SelectedProductsTable } from ".";

const { Text } = Typography;

interface ConvertToVirtualKitModalProps {
  organizationId: string;
  product: Product;
  isModalVisible: boolean;
  onCancel: () => unknown;
  onConvertingProduct: () => unknown;
  onRefetchProduct: () => unknown;
}

export type SelectedBaseProduct = Pick<Product, "id" | "name" | "sku"> & {
  quantity: number;
};

const ConvertToVirtualKitModal: React.FC<ConvertToVirtualKitModalProps> = ({
  organizationId,
  product,
  isModalVisible,
  onCancel,
  onConvertingProduct,
  onRefetchProduct,
}) => {
  const [search, setSearch] = useState("");
  const debouncedSearchValue = useDebounce(search, 500);
  const [selectedProducts, setSelectedProducts] = useState<SelectedBaseProduct[]>([]);

  const productIndex = useQuery(
    ["productIndex", debouncedSearchValue, organizationId],
    () =>
      fetchProductsIndex({
        q: debouncedSearchValue,
        organization_id: organizationId,
        product_type: "base",
        active: true,
      }),
    {
      enabled: !!(debouncedSearchValue && organizationId),
    }
  );

  const products = useMemo(() => {
    // filter out product thats about to be converted
    return productIndex.data?.data?.filter((p) => p.id !== product.id) ?? [];
  }, [productIndex.data, product.id]);

  const { mutate: convertToVirtualKitMutation, isLoading: isConvertingProduct } = useMutation(convertToVirtualKit, {
    onMutate: onConvertingProduct,
    onSuccess: ({ recipient_email }) => {
      message.success(
        `We're processing your conversion request. We will send an email to ${recipient_email} when it's completed.`
      );
    },
    onSettled: () => {
      onRefetchProduct();
    },
  });

  const isConvertDisabled = !selectedProducts.length || isConvertingProduct;

  const submitConvertBaseProduct = () => {
    const kitProducts = selectedProducts.reduce(
      (acc, selectedProduct) => ({
        ...acc,
        [selectedProduct.id]: selectedProduct.quantity,
      }),
      {}
    );

    convertToVirtualKitMutation({
      productID: product.id,
      body: {
        kit_products: kitProducts,
      },
    });
  };

  const handleProductQuantityChanged = (productID: string, quantity = 1) => {
    setSelectedProducts((products) =>
      products.map((p) => ({
        ...p,
        quantity: p.id === productID ? quantity : p.quantity,
      }))
    );
  };

  const handleSelectProduct = (productID: string) => {
    const existingProduct = selectedProducts.find((p) => p.id === productID);
    if (existingProduct) {
      handleProductQuantityChanged(existingProduct.id, existingProduct.quantity + 1);
    } else {
      const product = products.find((p) => p.id === productID);
      if (!product) return message.error("Product not found");
      setSelectedProducts((products) => [
        ...products,
        {
          ...product,
          quantity: 1,
        },
      ]);
    }
    setSearch("");
  };

  const onProductRemoved = (productID: string) => {
    setSelectedProducts((products) => products.filter((p) => p.id !== productID));
  };

  return (
    <Modal
      visible={isModalVisible}
      title={"Convert to Virtual Kit?"}
      okButtonProps={{
        disabled: isConvertDisabled,
        loading: isConvertingProduct,
      }}
      okText={"Convert"}
      onOk={submitConvertBaseProduct}
      onCancel={onCancel}
      cancelButtonProps={{
        disabled: isConvertDisabled,
        loading: isConvertingProduct,
      }}
      destroyOnClose
      width={1000}
      bodyStyle={{ textAlign: "center" }}
    >
      <Space direction="vertical">
        <S.SearchWrapper>
          <Text>
            <b>Base Products can only be converted to Virtual Kits once.</b>
          </Text>
          <Select
            showSearch
            allowClear
            value={search}
            style={{ width: "100%" }}
            defaultActiveFirstOption={false}
            filterOption={false}
            onSearch={setSearch}
            suffixIcon={<SearchOutlined style={{ color: colors.GREY }} />}
            onSelect={handleSelectProduct}
            loading={productIndex.isLoading}
            dropdownRender={(menu) => (
              <>
                <S.Flex style={{ padding: ".5em 1em 0 1em" }}>
                  <S.DropdownText bold>Product Name</S.DropdownText>
                  <S.DropdownText bold>SKU</S.DropdownText>
                </S.Flex>
                <hr color={colors.GREY_LIGHT} />
                <Spin spinning={productIndex.isLoading} tip="Please wait...">
                  {menu}
                </Spin>
              </>
            )}
          >
            {products?.map((product) => (
              <S.Option key={product.id} value={product.id ?? ""}>
                <S.Flex title={product.name}>
                  <S.DropdownText>{product.name}</S.DropdownText>
                  <S.DropdownText>{product.sku}</S.DropdownText>
                </S.Flex>
              </S.Option>
            ))}
          </Select>
          <Text type="secondary">Search base products to add to Virtual Kit</Text>
          <SelectedProductsTable
            selectedProducts={selectedProducts}
            disabled={isConvertDisabled}
            onProductRemoved={onProductRemoved}
            onProductQuantityChanged={handleProductQuantityChanged}
          />
        </S.SearchWrapper>
      </Space>
    </Modal>
  );
};

export { ConvertToVirtualKitModal };
